import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

const dot = (bg, ring) => L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:${bg};border:3px solid ${ring};box-shadow:0 1px 5px rgba(0,0,0,.4)"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});
const bienIcon = dot("#121311", "#c9e86a");
const refIcon = dot("#c9e86a", "#121311");

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => { map.setView(center, zoom); }, [center[0], center[1], zoom]); // eslint-disable-line
  return null;
}

// Regroupement (cluster) des référents proches + popup cliquable (fiche / missionner).
function RefCluster({ referents, onDetail, onMission }) {
  const map = useMap();
  const cbRef = useRef({ onDetail, onMission });
  cbRef.current = { onDetail, onMission };
  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 45,
      spiderfyOnMaxZoom: true,
    });
    referents.filter((r) => r.map_lat && r.map_lng).forEach((r) => {
      const m = L.marker([r.map_lat, r.map_lng], { icon: refIcon });
      const dist = r.distance_km != null ? `<div style="color:#5a7d2a;font-size:11px;margin-top:2px">à ${r.distance_km} km</div>` : "";
      const rating = r.avg_rating ? `<div style="color:#26301c;font-size:11px">★ ${r.avg_rating}/5 (${r.review_count} avis)</div>` : `<div style="color:#8a9a78;font-size:11px">Nouveau</div>`;
      const contract = r.contract_signed ? `<div style="color:#059669;font-size:11px;margin-top:2px">✓ Contrat signé</div>` : "";
      const btn = "display:inline-block;padding:6px 10px;border-radius:9999px;font-size:12px;font-weight:600;cursor:pointer;text-align:center";
      m.bindPopup(
        `<div style="min-width:170px;font-family:inherit">
          <div style="font-weight:700;color:#121311;font-size:13px">${esc(r.name)}</div>
          <div style="color:#5b6b4a;font-size:11px">${esc(r.profession || "Référent")}${r.city ? " · " + esc(r.city) : ""}</div>
          ${dist}${rating}${contract}
          <div style="display:flex;gap:6px;margin-top:8px">
            <button data-act="detail" style="${btn};border:1px solid #d9e0c8;background:#fff;color:#2f4a1f">Voir la fiche</button>
            <button data-act="mission" style="${btn};border:none;background:#121311;color:#c9e86a">Missionner</button>
          </div>
        </div>`,
        { minWidth: 180 }
      );
      m.on("popupopen", (e) => {
        const el = e.popup.getElement();
        if (!el) return;
        const d = el.querySelector('[data-act="detail"]');
        const s = el.querySelector('[data-act="mission"]');
        if (d) d.onclick = () => { map.closePopup(); cbRef.current.onDetail && cbRef.current.onDetail(r.id); };
        if (s) s.onclick = () => { map.closePopup(); cbRef.current.onMission && cbRef.current.onMission(r); };
      });
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [referents]); // eslint-disable-line
  return null;
}

export function ReferentsMap({ referents = [], bienPoints = [], radiusKm = 0, onDetail, onMission }) {
  const refPts = referents.filter((r) => r.map_lat && r.map_lng);
  const pts = [...bienPoints.map((b) => [b.lat, b.lng]), ...refPts.map((r) => [r.map_lat, r.map_lng])];
  const center = useMemo(() => {
    if (!pts.length) return [46.6, 2.4];
    return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
  }, [JSON.stringify(pts)]); // eslint-disable-line
  const zoom = !pts.length ? 5 : (bienPoints.length && radiusKm ? 9 : (pts.length === 1 ? 11 : 6));
  return (
    <div className="rounded-2xl overflow-hidden border border-brand-soft" style={{ height: 320 }} data-testid="referents-map">
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <Recenter center={center} zoom={zoom} />
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {bienPoints.map((b, i) => (
          <React.Fragment key={`b-${i}`}>
            <Marker position={[b.lat, b.lng]} icon={bienIcon}><Popup>Votre bien · {b.label}</Popup></Marker>
            {radiusKm > 0 && <Circle center={[b.lat, b.lng]} radius={radiusKm * 1000} pathOptions={{ color: "#5a7d2a", weight: 1, fillColor: "#c9e86a", fillOpacity: 0.08 }} />}
          </React.Fragment>
        ))}
        <RefCluster referents={refPts} onDetail={onDetail} onMission={onMission} />
      </MapContainer>
    </div>
  );
}
